/**
 * Cliente HTTP de la API.
 *
 * Toda llamada al servidor pasa por `solicitar`: agrega el prefijo `/api`, envía y recibe JSON,
 * transforma los errores de la API en un `ErrorDeApi` con código estable, y (opcional) valida la
 * respuesta con el esquema del contrato compartido. Si la API cambia de forma sin avisar, falla
 * aquí con un mensaje claro y no más adelante con un error confuso en la pantalla.
 */
import { errorApiSchema } from '@cdtec-backlog/contrato';
import type { z } from 'zod';

export class ErrorDeApi extends Error {
  constructor(
    readonly estado: number,
    readonly codigo: string,
    mensaje: string,
    readonly detalles: { campo: string; mensaje: string }[] = [],
  ) {
    super(mensaje);
    this.name = 'ErrorDeApi';
  }
}

interface Opciones<E extends z.ZodType | undefined> {
  metodo?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  cuerpo?: unknown;
  /** Si se entrega, la respuesta se valida y se tipa con este esquema. */
  esquema?: E;
}

export async function solicitar<E extends z.ZodType | undefined = undefined>(
  ruta: string,
  { metodo = 'GET', cuerpo, esquema }: Opciones<E> = {},
): Promise<E extends z.ZodType ? z.output<E> : undefined> {
  let respuesta: Response;
  try {
    respuesta = await fetch(`/api${ruta}`, {
      method: metodo,
      credentials: 'same-origin',
      headers: cuerpo === undefined ? {} : { 'Content-Type': 'application/json' },
      ...(cuerpo === undefined ? {} : { body: JSON.stringify(cuerpo) }),
    });
  } catch {
    throw new ErrorDeApi(
      0,
      'sin_conexion',
      'No hay conexión con el servidor. Revisa tu red e intenta de nuevo.',
    );
  }

  if (!respuesta.ok) throw await errorDesde(respuesta);
  if (respuesta.status === 204 || esquema === undefined) {
    return undefined as E extends z.ZodType ? z.output<E> : undefined;
  }
  return esquema.parse(await respuesta.json()) as E extends z.ZodType ? z.output<E> : undefined;
}

/**
 * Sube un archivo como cuerpo binario crudo (sin multipart) con el tipo indicado.
 * Hermana de `solicitar`: mismo prefijo `/api`, mismos errores y misma validación de la respuesta.
 *
 * Usa `XMLHttpRequest` y no `fetch` por una sola razón: `fetch` no informa cuánto se ha enviado, y la
 * barra de progreso de los adjuntos (`alProgresar`, fracción de 0 a 1) necesita ese dato. La cookie de
 * sesión viaja igual (mismo origen) y el navegador agrega `Origin` como en cualquier POST.
 */
export function subirArchivo<E extends z.ZodType>(
  ruta: string,
  archivo: Blob,
  {
    tipo,
    esquema,
    alProgresar,
  }: { tipo: string; esquema: E; alProgresar?: (fraccion: number) => void },
): Promise<z.output<E>> {
  const sinConexion = () =>
    new ErrorDeApi(
      0,
      'sin_conexion',
      'No hay conexión con el servidor. Revisa tu red e intenta de nuevo.',
    );
  return new Promise<z.output<E>>((resolver, rechazar) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api${ruta}`);
    xhr.setRequestHeader('Content-Type', tipo);
    xhr.upload.onprogress = (evento) => {
      if (evento.lengthComputable && evento.total > 0) alProgresar?.(evento.loaded / evento.total);
    };
    xhr.onerror = () => rechazar(sinConexion());
    xhr.onabort = () => rechazar(sinConexion());
    xhr.onload = () => {
      // Misma lectura de la respuesta que `solicitar` (errores con código, validación con el esquema).
      const respuesta = new Response(xhr.status === 204 ? null : xhr.responseText, {
        status: xhr.status,
      });
      if (!respuesta.ok) {
        void errorDesde(respuesta).then(rechazar);
        return;
      }
      respuesta
        .json()
        .then((cuerpo) => {
          alProgresar?.(1);
          resolver(esquema.parse(cuerpo));
        })
        .catch(rechazar);
    };
    xhr.send(archivo);
  });
}

/**
 * Lee una respuesta como texto (UTF-8), con un tope de bytes: sirve para previsualizar un adjunto
 * `.txt` o `.csv` sin cargar en pantalla un archivo enorme. `truncado` avisa que había más.
 * Hermana de `solicitar`: mismo prefijo `/api` y mismos errores. El texto se muestra con
 * `textContent` (React), nunca como HTML.
 */
export async function solicitarTexto(
  ruta: string,
  { maxBytes }: { maxBytes: number },
): Promise<{ texto: string; truncado: boolean }> {
  let respuesta: Response;
  try {
    respuesta = await fetch(`/api${ruta}`, { method: 'GET', credentials: 'same-origin' });
  } catch {
    throw new ErrorDeApi(
      0,
      'sin_conexion',
      'No hay conexión con el servidor. Revisa tu red e intenta de nuevo.',
    );
  }
  if (!respuesta.ok) throw await errorDesde(respuesta);
  const bytes = new Uint8Array(await respuesta.arrayBuffer());
  const truncado = bytes.byteLength > maxBytes;
  // `stream: true` deja fuera un carácter partido por el corte (sin «�» al final).
  const texto = new TextDecoder('utf-8').decode(bytes.subarray(0, maxBytes), {
    stream: truncado,
  });
  return { texto, truncado };
}

async function errorDesde(respuesta: Response): Promise<ErrorDeApi> {
  try {
    const cuerpo = errorApiSchema.safeParse(await respuesta.json());
    if (cuerpo.success) {
      const { codigo, mensaje, detalles } = cuerpo.data.error;
      return new ErrorDeApi(respuesta.status, codigo, mensaje, detalles);
    }
  } catch {
    // El cuerpo no era JSON (por ejemplo, una página de error de nginx): se cae al mensaje genérico.
  }
  return new ErrorDeApi(
    respuesta.status,
    'error_desconocido',
    `El servidor respondió con un error (${respuesta.status}).`,
  );
}

/** Mensaje apto para mostrar a una persona, venga de la API o de un fallo inesperado. */
export function mensajeDeError(error: unknown): string {
  if (error instanceof ErrorDeApi) {
    const primerDetalle = error.detalles[0];
    return primerDetalle ? `${error.message} ${primerDetalle.mensaje}` : error.message;
  }
  return 'Ocurrió un error inesperado. Intenta de nuevo.';
}

/** Mensaje de un campo concreto: el detalle de la API si lo trae (sin repetir el general), si no el mensaje de siempre. */
export function mensajeDeCampo(error: unknown): string {
  if (error instanceof ErrorDeApi && error.detalles[0]) return error.detalles[0].mensaje;
  return mensajeDeError(error);
}
